import { z } from 'zod/v4';
import { LSFavouriteLocationSchema, type LSFavouriteLocation } from '#shared/types/location';
import { LSSuburbSchema } from '#shared/types/suburb';
import { LSLocalitySchema } from '#shared/types/locality';

const KEY = 'lcp-favourite-locations';

const BackendLocationSchema = z
	.object({
		s: LSSuburbSchema.partial(),
		l: LSFavouriteLocationSchema,
		lo: LSLocalitySchema.partial(),
	})
	.transform((obj) => obj.l);

export function useFavouriteLocationStore() {
	const { $api } = useNuxtApp();

	const locationsMap = useState<Map<string, LSFavouriteLocation[]>>(KEY, () => new Map());
	const fetching = ref(false);

	async function fetchBackendLocations(localityId: string): Promise<LSFavouriteLocation[]> {
		const raw = await $api<unknown[]>('/api/Locations/getLocations_backend', {
			method: 'POST',
			body: { localityId, suburbPartial: '' },
		});
		const parsed = BackendLocationSchema.array().safeParse(raw);
		if (parsed.success) return parsed.data;
		frogger.warn('favourite locations validation failed — using empty list', {
			localityId,
			issues: parsed.error.issues,
		});
		return [];
	}

	async function fetchAll(localities: { localityId: string }[]) {
		if (!localities?.length) return;

		fetching.value = true;
		try {
			const results = await Promise.all(
				localities.map(async (locality) => {
					try {
						const locations = await fetchBackendLocations(locality.localityId);
						return { localityId: locality.localityId, locations };
					} catch (error) {
						frogger.warn('favourite locations fetch failed — using empty list', error, {
							localityId: locality.localityId,
						});
						return {
							localityId: locality.localityId,
							locations: [] as LSFavouriteLocation[],
						};
					}
				}),
			);

			const newMap = new Map(locationsMap.value);
			for (const { localityId, locations } of results) {
				newMap.set(localityId, locations);
			}
			locationsMap.value = newMap;
		} finally {
			fetching.value = false;
		}
	}

	async function refreshLocality(localityId: string) {
		try {
			const locations = await fetchBackendLocations(localityId);
			const newMap = new Map(locationsMap.value);
			newMap.set(localityId, locations);
			locationsMap.value = newMap;
		} catch (error) {
			frogger.warn('favourite locations refresh failed', error, { localityId });
		}
	}

	function getLocationsForLocality(localityId: string) {
		return locationsMap.value.get(localityId) || [];
	}

	const allLocations = computed(() => {
		const all: LSFavouriteLocation[] = [];
		locationsMap.value.forEach((locations) => all.push(...locations));
		return all;
	});

	return {
		data: readonly(locationsMap),
		allLocations,
		fetching: readonly(fetching),
		fetchAll,
		refreshLocality,
		getLocationsForLocality,
	};
}
